import React, { useMemo, useState, useEffect } from "react";
import { useLocation, useParams } from "react-router";
import { useSmartNavigateLegacy as useNavigate } from "components/SmartLink";
import { Col, Row, Tab, Tabs } from "react-bootstrap";
import { PageTitle } from "domain/feature/hub/pageTitle/styles";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Wrapper, CancelButton, IconButton } from "./index.style";
import { useTrackBreadcrumb } from "hooks/useTrackBreadcrumb";
import { TrackTable, TableColumn } from "./components/TrackTable";
import { StatusBadge } from "./components/TrackTable/style";
import { trackService, TrackDetailResponse, TrackSubscriptionsResponse, TrackSubscriptionItem } from "data/services/trackService";
import { showConfirmationPopUp } from "components/popup/confirmationModal";
import { showToast } from "components/toast";
import { Loading } from "components/loading";

interface SubscriberData {
  id: number;
  nome: string;
  aulaAtual: string;
  progresso: number;
  status: "Em andamento" | "Concluído" | "Inscrito";
}

export const TrackGetPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation() as any;
  const { trackId } = useParams();
  const [activeKey, setActiveKey] = useState<"subscribers" | "questions" | "uploads">("subscribers");
  const [trackData, setTrackData] = useState<TrackDetailResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [subscriptions, setSubscriptions] = useState<TrackSubscriptionItem[] | null>(null);
  const [page, setPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalRows, setTotalRows] = useState<number>(0);
  const [search, setSearch] = useState<string>("");
  const itemsPerPage = 20;

  useEffect(() => {
    const fetchTrackData = async () => {
      if (!trackId) return;

      try {
        setIsLoading(true);
        setError(null);
        const [data, subs]: [TrackDetailResponse, TrackSubscriptionsResponse] = await Promise.all([
          trackService.getTrackDetails(trackId),
          trackService.getTrackSubscriptions(trackId, {
            page: page - 1,
            limit: itemsPerPage,
            search: search
          })
        ]);

        setTrackData(data);
        setSubscriptions(subs?.rows || []);
        setTotalPages(subs?.totalPages || 1);
        setTotalRows(subs?.totalRows || 0);
      } catch (err: any) {
        console.error('Erro ao buscar dados da trilha:', err);
        setError(err?.response?.data?.message || 'Erro ao carregar trilha');
      } finally {
        setIsLoading(false);
      }
    };

    fetchTrackData();
  }, [trackId, page, search]);

  const mapApiStatusToPt = (status: string): SubscriberData['status'] => {
    switch (status) {
      case 'InProgress':
        return 'Em andamento';
      case 'Completed':
        return 'Concluído';
      case 'Subscribed':
        return 'Inscrito';
      default:
        return 'Inscrito';
    }
  };


  const inscritosColumns: TableColumn<SubscriberData>[] = [
    { key: 'nome', title: 'Nome' },
    { key: 'aulaAtual', title: 'Aula atual' },
    { key: 'progresso', title: 'Progresso', render: (value: number) => (
        <span>{Math.round(Math.min(Math.max(value || 0, 0), 100))}%</span>
    ) },
    {
      key: 'status',
      title: 'Status',
      render: (value: string) => (
        <StatusBadge className={value.toLowerCase().replace(' ', '-')}>
          {value}
        </StatusBadge>
      )
    }
  ];



  const trackTitle = useMemo(() => {
    if (trackData?.title) return trackData.title;
    const stateTitle = location?.state?.track?.title as string | undefined;
    if (stateTitle && stateTitle.trim() !== "") return stateTitle;
    return "Carregando...";
  }, [trackData, location?.state]);

  useTrackBreadcrumb(trackTitle);

  const togglePublish = (): void => {
    if (!trackId || !trackData) return;
    const isCurrentlyPublished = trackData.status === 'Publish';
    const action = isCurrentlyPublished ? 'cancelar a publicação' : 'publicar';
    const message = isCurrentlyPublished
      ? `Deseja realmente voltar a trilha "${trackData.title}" para rascunho?`
      : `Deseja realmente publicar a trilha "${trackData.title}"?`;

    showConfirmationPopUp({
      title: isCurrentlyPublished ? "Cancelar publicação" : "Publicar trilha",
      message: message,
      onConfirm: async () => {
        try {
          const response = await trackService.togglePublishTrack(trackId);
          const newStatus = response.status === 'Publish' ? 'Publish' : 'Draft';
          setTrackData(prev => prev ? { ...prev, status: newStatus } : null);
          if (newStatus === 'Draft') {
            showToast('success', 'Trilha voltou para rascunho');
          } else {
            showToast('success', 'Trilha publicada com sucesso!');
          }
        } catch (err) {
          console.error('Erro ao alterar publicação da trilha:', err);
          showToast('error', `Não foi possível ${action} da trilha`);
        }
      }
    });
  };
  const edit = (): void => {
    if (trackId) {
      navigate(`/hub/tracks/${trackId}/edit`);
    }
  };

  const remove = (): void => {
    if (!trackId || !trackData) return;

    showConfirmationPopUp({
      title: "Excluir Trilha",
      message: `Você deseja realmente excluir a trilha "${trackData.title}"?`,
      onConfirm: async () => {
        try {
          await trackService.deleteTrack(trackId);
          showToast('success', 'Trilha excluída com sucesso!');
          navigate('/hub/tracks');
        } catch (error) {
          console.error("Erro ao excluir trilha:", error);
          showToast('error', 'Erro ao excluir trilha');
        }
      }
    });
  };

  if (isLoading) {
    return (
      <Wrapper>
        <Row className="align-items-center mb-2">
          <Col>
            <PageTitle>Trilha</PageTitle>
          </Col>
        </Row>
        <Loading />
      </Wrapper>
    );
  }

  if (error) {
    return (
      <Wrapper>
        <Row className="align-items-center mb-2">
          <Col>
            <PageTitle>Erro</PageTitle>
          </Col>
        </Row>
        <div className="text-center py-5">
          <FontAwesomeIcon icon={["fas", "exclamation-triangle"]} className="me-2 text-warning" />
          <p className="text-danger">{error}</p>
          <button className="btn btn-outline-primary" onClick={() => navigate('/hub/tracks')}>
            Voltar para trilhas
          </button>
        </div>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <div data-track-name style={{ display: 'none' }}>{trackTitle}</div>
      <Row className="align-items-center mb-2 g-0 gap-0">
        <Col>
          <PageTitle>{trackTitle}</PageTitle>
        </Col>
        <div className="w-100 d-md-none" />
        <Col xs="auto" className="d-flex align-items-center gap-2">
          <CancelButton onClick={togglePublish}>
            {trackData?.status === 'Publish' ? 'Cancelar publicação' : 'Publicar'}
          </CancelButton>
          <IconButton onClick={edit}>
            <FontAwesomeIcon icon={["fas", "pen"]} />
          </IconButton>
          <IconButton onClick={remove}>
            <FontAwesomeIcon icon={["fas", "trash"]} />
          </IconButton>
        </Col>
      </Row>

      <Tabs
        id="track-get-tabs"
        activeKey={activeKey}
        onSelect={(k) => k && setActiveKey(k as any)}
        className="mb-3"
      >
        <Tab eventKey="subscribers" title={`Inscritos (${totalRows})`}>
        </Tab>
      </Tabs>


      <div className="mt-4">
        {activeKey === "subscribers" && (
          <TrackTable
            data={(subscriptions || []).map((item, index) => ({
              id: index + 1,
              nome: item.appUserName || '—',
              aulaAtual: item.currentLessonLabel || '—',
              progresso: item.progress,
              status: mapApiStatusToPt(item.status)
            }))}
            columns={inscritosColumns}
            itemsPerPage={itemsPerPage}
            serverMode={{
              totalPages,
              currentPage: page,
              onPageChange: (p) => setPage(p),
              searchTerm: search,
              onSearch: (term) => { setSearch(term); setPage(1); },
              itemsPerPageText: `${itemsPerPage} itens por página`
            }}
          />
        )}
      </div>
    </Wrapper>
  );
};


